import _, { endsWith, filter, find, map, size, some, toNumber } from 'lodash';
import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, FormGroup, FormText, Input, InputGroup, InputGroupText, Label } from 'reactstrap';
import { DeepPartial } from 'tsdef';
import { gql, useQuery } from 'urql';
import { Group, HacpackQuery } from '../../types/graph';
import { useCurrentUser, useId } from '../../hooks';
import { RoleName } from '../../types';

const VIRTUAL_DOMAIN = process.env.REACT_APP_VIRTUAL_USER_DOMAIN;

const usersQuery = gql<HacpackQuery, {}>`query {
    users {
        id
        login
        email
        displayName
        roles {
            id
            name
            group {
                name
                id
            }
        }
    }
}`;

type AddUserFormSubmitValue = { email: string | null, userId: number | null, message: string | null, roleIds: number[] | null, forceEmail: boolean | null, managerId: number | null };

type AddUserFormProps = {
    group: DeepPartial<Group>,
    onSubmit?: (data: AddUserFormSubmitValue) => void,
    onUndo?: () => void
};

const USERNAME_MINLENGTH = 5;

const AddUserForm = ({ group, onSubmit, onUndo }: AddUserFormProps) => {
    const { t } = useTranslation('common');
    const { id } = useId(5);
    const { currentUser } = useCurrentUser();

    const [usersResult/*, reexecuteUsersQuery*/] = useQuery({
        query: usersQuery
    });

    const { data/*, fetching: fetchingUsers*/ } = usersResult;

    //const users = useMemo(() => filter(data?.users, (u) => !some(u.roles, r => r.group.id === group.id)), [data, group]);

    const [userId, setUserId] = useState<number | null>(null);
    const [emailOrUsername, setEmailOrUsername] = useState('');
    const [message, setMessage] = useState<string>('');
    const [roleIds, setRoleIds] = useState<number[]>([]);
    const [managerId/*, setManagerId*/] = useState<number>(currentUser?.id as number);
    const [isVirtualUser, setIsVirtualUser] = useState<boolean>(false);
    const [forceEmail, setForceEmail] = useState<boolean>(true);

    const [dropdownOpen, setDropdownOpen] = useState(false);

    const toggleDropDown = useCallback(() => setDropdownOpen(!dropdownOpen), [setDropdownOpen, dropdownOpen]);

    const isValid = useMemo(() => size(roleIds) > 0 && (userId !== null || size(emailOrUsername) >= USERNAME_MINLENGTH), [userId, emailOrUsername, roleIds]);

    const submitDisabled = !isValid;

    const users = data?.users;

    // @ts-ignore
    const selectableUsers = useMemo(() => filter(users, ({ email, roles }) => !some(roles, r => r.group.id === group.id) && !endsWith(email, `@${VIRTUAL_DOMAIN}`)), [users, group]);
    //const selectableManagers = useMemo(() => filter(users, ({ roles }) => some(roles, r => r.name === RoleName.SUPERUSER || (r.group.id === group.id && r.name === RoleName.ADMIN))), [users, group]);

    const handleSubmit = useCallback((e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const email = isVirtualUser === true ? `${emailOrUsername}@${VIRTUAL_DOMAIN}` : emailOrUsername

        onSubmit?.({ userId, email, roleIds, message, forceEmail, managerId: isVirtualUser === true ? managerId : null })
    }, [userId, emailOrUsername, roleIds, message, forceEmail, managerId, onSubmit, isVirtualUser]);

    useEffect(() => {
        if (isVirtualUser === false) {
            return;
        }

        setUserId(userId => {
            if (userId !== null) {
                setEmailOrUsername(emailOrUsername => {
                    return '';
                })
            }
            return null;
        });
        setForceEmail(true);
        setRoleIds(_(group.roles).filter(['name', RoleName.USER]).map('id').value() as number[])
    }, [isVirtualUser])

    //useEffect(() => {
    //    if (isVirtualUser === false) {
    //        return;
    //    }

    //    setForceEmail(true);
    //}, [isVirtualUser])

    useEffect(() => {
        if (isVirtualUser === true) {
            return;
        }
        setUserId(find(selectableUsers, ['email', emailOrUsername])?.id ?? null);
    }, [emailOrUsername, isVirtualUser, setUserId, selectableUsers])

    const emailOrUsernameInputId = `emailOrUsername${id}`;
    //const managerInputId = `manager${id}`;

    const userInput = useMemo(() => {
        if (isVirtualUser) {
            return <InputGroup>
                <Input placeholder="Nome nuovo utente..." pattern="[A-Za-z0-9-_.]+" minLength={USERNAME_MINLENGTH} type="text" value={emailOrUsername} onChange={e => setEmailOrUsername(e.target.value)} required />
                <InputGroupText>@{VIRTUAL_DOMAIN}</InputGroupText>
            </InputGroup>
        }

        //const user = userId === null ? null : find(users, { id: userId }) ?? null;

        const datalistId = `selectableUsers${id}`

        const input = //user === null ? 
            <input id={emailOrUsernameInputId} list={datalistId} className="form-control" placeholder="Email nuovo utente..." type="email" value={emailOrUsername} onChange={e => setEmailOrUsername(e.target.value)} autoComplete="on" required />
        //: <div className="form-control">{user.displayName} ({user.email})</div>;

        if (selectableUsers.length === 0) {
            return input;
        }
        //<InputGroupButtonDropdown addonType="append" isOpen={dropdownOpen} toggle={toggleDropDown}>
        //    <DropdownToggle caret>
        //    </DropdownToggle>
        //    <DropdownMenu>
        //        <DropdownItem onClick={() => setUserId(null)}>{t('Email nuovo utente...')}</DropdownItem>
        //        <DropdownItem divider />
        //        {selectableUsers.map(({ id, email, displayName }) =>
        //            <DropdownItem key={id} onClick={() => setUserId(id as number)}>{displayName} ({email})</DropdownItem>
        //        )}
        //    </DropdownMenu>
        //</InputGroupButtonDropdown>
        return <>
            <datalist id={datalistId}>
                {selectableUsers.map(({ email }) =>
                    <option key={email} value={email} />
                )}
            </datalist>
            {input}
        </>;
    }, [id, userId, selectableUsers, emailOrUsername, setEmailOrUsername, dropdownOpen, toggleDropDown, isVirtualUser, emailOrUsernameInputId]);

    //const selectableManagersOptions = useMemo(() => selectableManagers.map(({ id, email, displayName }) =>
    //    <option key={id} value={id}>{displayName} ({email})</option>
    //), [selectableManagers])

    const messageInput = useMemo(() => {
        if (userId !== null) {
            return null;
        }

        return <FormGroup>
            <Label>{t('Messaggio')}</Label>
            <textarea className="form-control" value={message} onChange={(e) => setMessage(e.target.value)} />
        </FormGroup>
    }, [message, setMessage, userId, emailOrUsernameInputId, t])

    // @ts-ignore
    const roleOptions = map(group.roles, ({ id, name }) => <option key={id} data-name={name} disabled={(isVirtualUser && name === "admin") || (forceEmail && name === 'authority')} value={id}>{t(`userManagement.role.${name}.name`)}</option>);

    return <Form onSubmit={handleSubmit} className="p-3">

        <FormGroup>
            <FormGroup>
                <Label for={emailOrUsernameInputId}>{t('Utente')}</Label>
                {userInput}
            </FormGroup>
        </FormGroup>

        <FormGroup check>
            <Label check>
                <Input type="checkbox" checked={isVirtualUser} onChange={(e) => setIsVirtualUser(e.target.checked)} />{' '}{t('utente virtuale')}
            </Label>{' '}
            <FormText>{t('Spunta questa casella se l\'utente non dispone di un indirizzo email. Tutte le comunizazione saranno inviate all\'utente scelto come gestore.')}</FormText>
        </FormGroup>

        {/*{isVirtualUser ? <FormGroup>*/}
        {/*    <Label for={managerInputId} value={managerId}>{t('Gestore')}</Label>*/}
        {/*    <Input type="select" name="managerId" id={managerInputId} value={managerId} onChange={(ev) => setManagerId(ev.target.valueAsNumber)}>*/}
        {/*        {selectableManagersOptions}*/}
        {/*    </Input>*/}
        {/*</FormGroup> : null}*/}

        {/*<fieldset disabled={userId !== null || isVirtualUser === true}>*/}
        {/*    <FormGroup check>*/}
        {/*        <Label check>*/}
        {/*            <Input type="checkbox" checked={forceEmail} onChange={(e) => setForceEmail(e.target.checked)} />{' '}{t('Forza indirizzo email')}*/}
        {/*        </Label>*/}
        {/*        <FormText>{t('Se spuntata l\'invito sarà valido solo per l\'indirizzo email indicato. Togliere la spunta se l\'invito è indirizzato a un utente esterno che potrebbe già essere registrato con un altro indirizzo.')}</FormText>*/}
        {/*    </FormGroup>*/}
        {/*</fieldset>*/}

        <fieldset disabled={userId !== null || isVirtualUser === true}>
            <Form>
                <FormGroup check inline>
                    <Label check>
                        <Input type="radio" checked={forceEmail} onChange={(e) => setForceEmail(e.target.checked)} />{' '}{t('utente interno')}
                    </Label>
                </FormGroup>
                <FormGroup check inline>
                    <Label check>
                        <Input type="radio" checked={!forceEmail} onChange={(e) => setForceEmail(!e.target.checked)} />{' '}{t('utente esterno')}
                    </Label>
                </FormGroup>
            </Form>
        </fieldset>

        <fieldset>
            <FormGroup>
                <Label>{t('userManagement.columns.roles.name')}</Label>
                <select required className="form-control" value={map(roleIds, id => id.toString())} onChange={(e) => setRoleIds(Array.from(e.target.selectedOptions, option => toNumber(option.value)))} multiple>
                    {roleOptions}
                </select>
                <small className="form-text text-muted">
                    Per selezionare più opzioni utilizzare <kbd>Ctrl+clic</kbd>.
                </small>
            </FormGroup>
        </fieldset>

        {messageInput}

        <Button color="primary" disabled={submitDisabled} type="submit">Invia</Button>
        <Button className="ml-2" color="secondary" type="button" onClick={() => onUndo?.()}>Annulla</Button>
    </Form>;
}

export default AddUserForm;